import {ButtonBase, Menu, Stack, Typography} from "@mui/material";
import {api, tenantApi} from "@core/api";
import {invalidateChatbot} from "@core/api/invalidateChatbot";
import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {setTenant} from "@core/store/slices/appSlice";
import {Tenant} from "@core/model";
import {AsyncImage} from "./AsyncImage";
import LoadingView from "./LoadingView";
import {Selectors} from "@core/store/selectors";

const TenantItem: React.FC<{
  tenant: Tenant;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({tenant, onClick}) => {
  return (
    <ButtonBase onClick={onClick} sx={{width: 200, p: 1}}>
      <AsyncImage
        id={tenant.img}
        size="contain"
        fallbackNode={
          <Typography
            variant={"h4"}
            align="center"
            sx={{color: tenant.primaryColor}}>
            {tenant.name}
          </Typography>
        }
        height={40}
      />
    </ButtonBase>
  );
};

const TenantSelector = () => {
  const currentTenant = useSelector(Selectors.tenant);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    data: tenants,
    isLoading,
    isFetching,
  } = tenantApi.useFindEntity("page=0&size=999");

  useEffect(() => {
    if (!currentTenant && tenants && tenants.content.length > 0) {
      handleSelect(tenants.content[0].id);
    }
  }, [currentTenant, tenants]);

  const handleSelect = (tenantId: string | undefined) => {
    setAnchorEl(null);
    const tenant = tenants?.content.find(c => c.id === tenantId);
    dispatch(setTenant(tenant));
    dispatch(
      api.util.invalidateTags([
        "asset",
        "task",
        "ticket",
        "audit",
        "user",
        "document",
        "measure",
        "artifact",
        "ticket",
        "call",
      ]),
    );
    invalidateChatbot(dispatch);
  };

  return (
    <Stack>
      <LoadingView loading={isLoading || isFetching} />
      {currentTenant && (
        <TenantItem
          tenant={currentTenant}
          onClick={e => setAnchorEl(e.currentTarget)}
        />
      )}
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}>
        <Stack gap={1}>
          {tenants?.content.map(c => (
            <TenantItem
              key={c.id}
              tenant={c}
              onClick={() => handleSelect(c.id)}
            />
          ))}
        </Stack>
      </Menu>
    </Stack>
  );
};

export {TenantSelector};
